/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

/**
 * Custom property carrying the measured width of one copy of the flow list.
 * The scroll keyframe shifts by exactly that, so the next copy lands where the
 * previous one started and the loop has no seam.
 *
 * It has to be a variable rather than a percentage of the track: the number of
 * copies depends on how many fit the viewport, so no fixed percentage of the
 * track is one whole copy.
 */
export const MARQUEE_SHIFT_VAR = '--prebuilt-marquee-shift';

/**
 * Alpha ramp applied across the row. Fully opaque across the middle ~62% of
 * the strip — which, with the strip itself at 80% of the window, is about half
 * the screen — and faded out to nothing by the edges, so it reads as centred
 * on the prompt box above it rather than as a band spanning the window.
 */
const CENTRE_FADE_MASK = [
  'linear-gradient(to right',
  'rgba(0,0,0,0) 0%',
  'rgba(0,0,0,0.12) 6%',
  'rgba(0,0,0,0.55) 12%',
  'rgba(0,0,0,1) 19%',
  'rgba(0,0,0,1) 81%',
  'rgba(0,0,0,0.55) 88%',
  'rgba(0,0,0,0.12) 94%',
  'rgba(0,0,0,0) 100%)',
].join(', ');

export default makeStyles((theme: Theme) =>
  createStyles({
    carouselRoot: {
      width: '100%',
      // The row is far wider than the strip and is clipped by it; the mask
      // below fades it out before the edge so it reads as drifting past rather
      // than as a band that starts and stops.
      overflow: 'hidden',
      // Room for each chip's border and its downward shadow. Both `overflow`
      // and `contain: paint` below clip at the padding box, so without this the
      // chip's border is shaved off against the edge of the strip.
      padding: theme.spacing(0.5, 0),
      boxSizing: 'border-box',
      maskImage: CENTRE_FADE_MASK,
      WebkitMaskImage: CENTRE_FADE_MASK,
      // Nothing inside can affect the layout or paint of anything outside, so
      // the browser can keep the strip's repaints to itself rather than
      // invalidating the page around it every frame.
      contain: 'layout paint',
    },
    // With the animation off there is only one copy, and the chips past the
    // edge would otherwise be unreachable by anything but the Tab key. The
    // strip becomes an ordinary scroller instead.
    carouselRootStatic: {
      overflowX: 'auto',
    },
    row: {
      display: 'flex',
      width: 'max-content',
      animation: '$scroll linear infinite',
      // Keeps the row on its own compositor layer: the track is far wider than
      // the window, and without this the browser repaints it on the main
      // thread every frame instead of just moving the layer.
      willChange: 'transform',
      // The chips are buttons, so the row has to stand still long enough to
      // press one. Hover covers the pointer; `focus-within` covers the Tab key,
      // which would otherwise walk the visitor onto a moving target. Done in
      // CSS rather than through React state on purpose — the row holds tens of
      // chips, and re-rendering them every time the pointer crosses the strip
      // was visible jank.
      '&:hover, &:focus-within': {
        animationPlayState: 'paused',
      },
    },
    // Off-screen the strip costs a compositor layer and an animation nobody
    // can see, on a track far wider than the window. Stopping it and giving
    // the layer back is the whole point of the observer that sets this.
    // Matched under `:hover` as well so the rule above, which is the more
    // specific selector, cannot restart a row that is out of view.
    rowPaused: {
      '&, &:hover, &:focus-within': {
        animationPlayState: 'paused',
      },
      willChange: 'auto',
    },
    // Reduced motion, or a browser that cannot measure: the row simply sits
    // there, and `carouselRootStatic` lets it be scrolled.
    rowStatic: {
      animation: 'none',
      willChange: 'auto',
      transform: 'none',
    },
    '@keyframes scroll': {
      from: { transform: 'translate3d(0, 0, 0)' },
      to: {
        transform: `translate3d(calc(var(${MARQUEE_SHIFT_VAR}, 0px) * -1), 0, 0)`,
      },
    },
    // One repetition of the flow list. The first one is measured and is the
    // one exposed to assistive technology and the Tab key; the others exist
    // only to fill the row.
    copy: {
      display: 'flex',
      flexShrink: 0,
    },
    // A chip holding the two applications a prebuilt integration connects, and
    // the direction between them. Every chip is the same size, which keeps the
    // row on a steady rhythm whatever the logos inside turn out to be.
    tile: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1),
      flexShrink: 0,
      width: theme.spacing(18),
      height: theme.spacing(7),
      boxSizing: 'border-box',
      // Spacing between chips is a margin rather than a `gap` on the row: the
      // measured copy width has to include the space that follows its last
      // chip, or consecutive copies would sit flush and the rhythm would break
      // at every seam.
      marginRight: theme.spacing(1.5),
      // Tighter across than down: the chip is a fixed width, so the narrower
      // sides are what give the two logos and the arrow between them room to
      // breathe instead of being squeezed towards the middle.
      padding: theme.spacing(1.5, 0.5),
      borderRadius: theme.spacing(2),
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0px 1px 2px 0px rgba(47,95,167,0.08)',
      cursor: 'pointer',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        borderColor: theme.palette.primary.light,
        boxShadow: '0px 4px 10px 0px rgba(47,95,167,0.16)',
      },
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
      },
    },
    tileLogo: {
      width: theme.spacing(3.5),
      height: theme.spacing(3.5),
      flexShrink: 0,
      // The logos are not drawn to a common bounding box, so they are fitted
      // rather than stretched.
      objectFit: 'contain',
    },
    arrow: {
      fontSize: theme.spacing(2),
      color: theme.palette.text.secondary,
      flexShrink: 0,
    },
    // Holds the strip's height while the catalogue is fetched, so the page
    // does not jump once the chips arrive.
    placeholder: {
      height: theme.spacing(8),
    },
  })
);
